// Колоните на „Дневник на резервациите" — КЛИЕНТСКИ режим: endpoint-ът
// връща целия период, затова подредбата, филтрите и редът „Общо" са в браузъра.
//
// Времената: „Час" носи ISO низа (за подредбата) и рисува „28.09.2026 19:30 –
// 21:30"; „Настанени в" / „Тръгнали в" са реалните моменти, „Реален престой" е
// разликата им в МИНУТИ (в колоната число, в клетката текст). Думите са тези
// от модула „Резервации" — „Име", „Забележка", „Настани".
// Статусът е чип със същите думи като в модула „Резервации" и чеклист с петте
// стойности — винаги всичките, дори периодът да няма отказани.

import { useMemo } from "react";
import { createColumnHelper, type Row } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { EM_DASH, parseDecimal } from "../../../../components/dataTable";
import type { ChecklistOption } from "../../../../components/dataTable";
import PhoneLink from "../../../../components/PhoneLink";
import type { ReservationJournalRow } from "../../../../api/reportsReservations";
import {
  RESERVATION_STATUSES,
  formatHour,
  formatMomentWithDate,
  formatReservationMinutes,
  reservationNoteText,
  statusChip,
  statusLabel,
} from "./reservationReportFormat";
import { SeatedByCell } from "./SeatedByCell";

const helper = createColumnHelper<ReservationJournalRow>();

type MinutesPicker = (row: ReservationJournalRow) => number | null;

/** Липсващите минути се държат като −∞: преди всичко при възходяща подредба. */
const minutesSorting =
  (pickMinutes: MinutesPicker) =>
  (rowA: Row<ReservationJournalRow>, rowB: Row<ReservationJournalRow>) =>
    (pickMinutes(rowA.original) ?? Number.NEGATIVE_INFINITY) -
    (pickMinutes(rowB.original) ?? Number.NEGATIVE_INFINITY);

const MONO = "font-pos-mono text-[12.5px] font-semibold tabular-nums";
// Като текстовата клетка на таблицата — телефонът само става връзка.
const PHONE_CELL = "text-[13px] font-medium text-ink";

export const useJournalColumns = () => {
  const { t } = useTranslation();

  const statusOptions = useMemo<ChecklistOption[]>(
    () =>
      RESERVATION_STATUSES.map((status) => ({
        value: statusLabel(t, status),
        label: statusLabel(t, status),
      })),
    [t],
  );

  return useMemo(
    () => [
      helper.accessor((row) => row.start_at, {
        id: "start_at",
        header: t("reportsPage.rvColumnTime"),
        size: 170,
        cell: (context) => (
          <span className={`${MONO} text-ink`}>
            {`${formatMomentWithDate(context.getValue())} – ${formatHour(
              context.row.original.end_at,
            )}`}
          </span>
        ),
        meta: {
          alwaysVisible: true,
          mobileRole: "meta",
          excel: { format: "text" },
        },
      }),

      helper.accessor((row) => row.guest_name, {
        id: "guest_name",
        header: t("reportsPage.rvColumnName"),
        size: 180,
        meta: {
          cell: { kind: "text" },
          filter: { kind: "text" },
          alwaysVisible: true,
          mobileRole: "title",
          excel: { format: "text" },
        },
      }),

      // Телефонът набира при клик — собствен рендер вместо `meta.cell`.
      helper.accessor((row) => row.phone, {
        id: "phone",
        header: t("reportsPage.rvColumnPhone"),
        size: 140,
        cell: (context) => (
          context.getValue() ? (
            <PhoneLink phone={context.getValue()} className={PHONE_CELL} />
          ) : (
            // Стар запис без телефон — „—" като празната текстова клетка.
            <span className="text-faint">{EM_DASH}</span>
          )
        ),
        meta: {
          filter: { kind: "text" },
          hideBelow: "tablet",
          excel: { format: "text" },
        },
      }),

      helper.accessor((row) => row.guests, {
        id: "guests",
        header: t("reportsPage.rvColumnGuests"),
        size: 76,
        meta: {
          cell: { kind: "num" },
          total: "sum",
          excel: { format: "number" },
        },
      }),

      helper.accessor((row) => row.table_names.join(", "), {
        id: "tables",
        header: t("reportsPage.rvColumnTables"),
        size: 110,
        meta: {
          cell: { kind: "text" },
          filter: { kind: "text" },
          mobileRole: "strong",
          excel: { format: "text" },
        },
      }),

      helper.accessor((row) => statusChip(t, row.status), {
        id: "status",
        header: t("reportsPage.rvColumnStatus"),
        size: 124,
        // Общият чеклист филтрира по етикета на чипа (`facetValueOf`) — затова
        // опциите са етикетите, не кодовете на статусите.
        meta: {
          cell: { kind: "chip" },
          filter: { kind: "checklist", options: statusOptions },
          alwaysVisible: true,
          excel: { format: "text" },
        },
      }),

      // „Забележка" — веднага след статуса и видима по подразбиране:
      // записаната при резервацията забележка е важна за човека, който чете
      // дневника. id-то НЕ е старото „note": запомнената подредба държеше
      // изгасената колона в края, а новото id влиза там, където е описано тук
      // (`mergeColumnOrder`).
      helper.accessor((row) => reservationNoteText(row.note) ?? "", {
        id: "reservation_note",
        header: t("reportsPage.rvColumnNote"),
        size: 200,
        meta: {
          cell: { kind: "text", fullTextOnHover: true },
          filter: { kind: "text" },
          excel: { format: "text" },
        },
      }),

      helper.accessor((row) => row.duration_minutes, {
        id: "duration_minutes",
        header: t("reportsPage.rvColumnDuration"),
        size: 120,
        cell: (context) => (
          <span className={`${MONO} text-ink`}>
            {formatReservationMinutes(t, context.getValue())}
          </span>
        ),
        meta: { align: "right", hideBelow: "tablet", excel: { format: "text" } },
      }),

      helper.accessor((row) => row.seated_at, {
        id: "seated_at",
        header: t("reportsPage.rvColumnSeatedAt"),
        size: 150,
        cell: (context) => (
          <span
            className={`${MONO} ${context.getValue() === null ? "text-faint" : "text-ink"}`}
          >
            {formatMomentWithDate(context.getValue())}
          </span>
        ),
        meta: { align: "right", hideBelow: "tablet", excel: { format: "text" } },
      }),

      helper.accessor((row) => row.released_at, {
        id: "released_at",
        header: t("reportsPage.rvColumnReleasedAt"),
        size: 150,
        cell: (context) => (
          <span
            className={`${MONO} ${context.getValue() === null ? "text-faint" : "text-ink"}`}
          >
            {formatMomentWithDate(context.getValue())}
          </span>
        ),
        meta: { align: "right", defaultHidden: true, excel: { format: "text" } },
      }),

      helper.accessor((row) => row.seated_minutes, {
        id: "seated_minutes",
        header: t("reportsPage.rvColumnStay"),
        size: 120,
        sortingFn: minutesSorting((row) => row.seated_minutes),
        cell: (context) => (
          <span
            className={`${MONO} ${
              context.getValue() === null ? "text-faint" : "text-ink"
            }`}
          >
            {formatReservationMinutes(t, context.getValue())}
          </span>
        ),
        meta: { align: "right", hideBelow: "tablet", excel: { format: "text" } },
      }),

      // Сметката липсва при предстояща/отказана/неизпълнена — `null` е тире, не нула, и не влиза в реда „Общо" като 0.
      // Стари данни (две резервации на една сметка): BE дава сумата само на
      // реда, който носи оборота — „Общо" = KPI „Оборот".
      helper.accessor(
        (row) => (row.bill_total === null ? null : parseDecimal(row.bill_total)),
        {
          id: "bill_total",
          header: t("reportsPage.rvColumnBill"),
          size: 110,
          meta: {
            cell: { kind: "money", mutedZero: true },
            total: "sum",
            excel: { format: "money" },
          },
        },
      ),

      helper.accessor((row) => row.created_by_name ?? "", {
        id: "created_by_name",
        header: t("reportsPage.rvColumnCreatedBy"),
        size: 140,
        meta: {
          cell: { kind: "text" },
          filter: { kind: "checklist" },
          hideBelow: "tablet",
          excel: { format: "text" },
        },
      }),

      // „Настанена от" — който направи първата поръчка по сметката (не
      // `seated_by`: прехвърлянето на сметката в Маркиране го нулира). id-то
      // остава старото — таблицата пази видимост/ширина/ред по него. Празната
      // клетка обяснява защо — виж SeatedByCell.
      helper.accessor((row) => row.first_order_by_name ?? "", {
        id: "seated_by_name",
        header: t("reportsPage.rvColumnSeatedBy"),
        size: 140,
        cell: (context) => <SeatedByCell row={context.row.original} />,
        meta: {
          filter: { kind: "checklist" },
          hideBelow: "tablet",
          excel: { format: "text" },
        },
      }),

      helper.accessor((row) => row.created_at, {
        id: "created_at",
        header: t("reportsPage.rvColumnCreatedAt"),
        size: 150,
        cell: (context) => (
          <span className={`${MONO} text-muted`}>
            {formatMomentWithDate(context.getValue())}
          </span>
        ),
        meta: { align: "right", defaultHidden: true, excel: { format: "text" } },
      }),
    ],
    [t, statusOptions],
  );
};
